/* ============================================================================
 * CALENDAR.EXE — where the fleet shows up
 * ----------------------------------------------------------------------------
 * Ported from the design source (SupercomputeA/supercompute-immersion →
 * app-calendar.html): the month grid, the event sheet, the door list with
 * check-in, the registration form, memories, guild roles and the guestbook.
 *
 * WHAT IS REAL: the month grid is a live calendar; registrations, memories and
 * guestbook entries are held in this browser. WHAT IS NOT, AND SAYS SO: the
 * event sheet and the door list are a specimen sheet — sample rooms, sample
 * tickets. Nothing here issues a Presence attestation.
 * ========================================================================= */

import { useMemo, useState } from 'react';
import { SkinArt } from '../components/SkinArt';

const TABS = [
  { id: 'month', label: 'Calendar' },
  { id: 'events', label: 'Events' },
  { id: 'eventgoer', label: 'Eventgoer' },
  { id: 'reg', label: 'Forms' },
  { id: 'memories', label: 'Memories' },
  { id: 'guild', label: 'Guild' },
  { id: 'guestbook', label: 'Guestbook' },
] as const;
type Tab = (typeof TABS)[number]['id'];

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EVENTS = [
  { name: 'Harbour session', venue: 'Harbour Room', date: '2026-10-18', seats: 180, state: 'confirmed' },
  { name: 'Kessel night', venue: 'Kessel', date: '2026-11-02', seats: 240, state: 'confirmed' },
  { name: 'School intake', venue: 'online', date: '2026-11-09', seats: 80, state: 'draft' },
  { name: 'Studio session', venue: 'Studio 9', date: '2026-11-23', seats: 120, state: 'holding' },
];

const NEXT_UP = [
  { name: 'Harbour Room, Lisbon', date: '18 OCT' },
  { name: 'Kessel, Berlin', date: '02 NOV' },
  { name: 'School intake, online', date: '09 NOV' },
  { name: 'Studio 9, Milan', date: '23 NOV' },
];

const GUESTS = [
  { ticket: 'GUEST-014', tier: 'guild', guild: true },
  { ticket: 'GUEST-015', tier: 'guild', guild: true },
  { ticket: 'GUEST-016', tier: 'guest', guild: false },
  { ticket: 'GUEST-017', tier: 'guest', guild: false },
  { ticket: 'GUEST-018', tier: 'guest', guild: false },
  { ticket: 'GUEST-019', tier: 'press', guild: false },
];

const GUILD_ROLES = [
  { role: 'Founder', gate: 'invite, manual', holders: 3, open: true },
  { role: 'Guild', gate: 'credential · event-attendance', holders: 48, open: true },
  { role: 'School', gate: 'credential · school-completion', holders: 12, open: false },
  { role: 'Press', gate: 'manual', holders: 4, open: true },
];

const MEMORIES = [
  { at: '18 OCT 2026', msg: 'Harbour session opened with the desk capture up on the wall.', tag: 'room' },
  { at: '02 NOV 2026', msg: 'Kessel: guestbook signed by 41 of 240.', tag: 'guestbook' },
  { at: '09 NOV 2026', msg: 'School intake, first cohort, 80 seats.', tag: 'school' },
];

interface Registration {
  handle: string;
  email: string;
  tier: string;
}
interface Memory {
  title: string;
  note: string;
}
interface GuestbookEntry {
  who: string;
  line: string;
  at: string;
}

function read<T>(key: string): T[] {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '[]') as T[];
  } catch {
    return [];
  }
}
function write<T>(key: string, v: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* non-fatal */
  }
}

const REGS_KEY = 'sc.calendar.regs';
const MEM_KEY = 'sc.calendar.memories';
const GB_KEY = 'sc.calendar.guestbook';

export function Calendar() {
  const [tab, setTab] = useState<Tab>('month');
  const [view, setView] = useState(() => new Date());
  const [checkedIn, setCheckedIn] = useState<string[]>([]);
  const [evFilter, setEvFilter] = useState('');
  const [ticketFilter, setTicketFilter] = useState('');

  const [regs, setRegs] = useState<Registration[]>(() => read<Registration>(REGS_KEY));
  const [rgHandle, setRgHandle] = useState('');
  const [rgMail, setRgMail] = useState('');
  const [rgTier, setRgTier] = useState('guest');
  const [rgConsent, setRgConsent] = useState(false);
  const [regOk, setRegOk] = useState(false);

  const [mems, setMems] = useState<Memory[]>(() => read<Memory>(MEM_KEY));
  const [memTitle, setMemTitle] = useState('');
  const [memNote, setMemNote] = useState('');

  const [book, setBook] = useState<GuestbookEntry[]>(() => read<GuestbookEntry>(GB_KEY));
  const [gbName, setGbName] = useState('');
  const [gbNote, setGbNote] = useState('');
  const [gbOk, setGbOk] = useState(false);

  const [guildCred, setGuildCred] = useState(true);
  const [pressManual, setPressManual] = useState(true);

  const today = new Date();
  const calLabel = `${MON[view.getMonth()]} ${view.getFullYear()}`;

  const cells = useMemo(() => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const first = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const out: (number | null)[] = [];
    for (let p = 0; p < first; p++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(d);
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const todayChip = `${DOW[today.getDay()]} ${today.getDate()} ${MON[today.getMonth()].slice(0, 3).toUpperCase()}`;

  const visibleEvents = EVENTS.filter((e) => (e.name + ' ' + e.venue).toLowerCase().includes(evFilter.toLowerCase()));
  const visibleGuests = GUESTS.filter((g) => g.ticket.toLowerCase().includes(ticketFilter.toLowerCase()));

  const toggleCheckIn = (ticket: string) =>
    setCheckedIn((prev) => (prev.includes(ticket) ? prev.filter((t) => t !== ticket) : [...prev, ticket]));

  const submitReg = () => {
    if (!rgConsent || rgHandle.trim().length < 2 || !/\S+@\S+\.\S+/.test(rgMail)) return;
    const next = [{ handle: rgHandle.trim(), email: rgMail.trim(), tier: rgTier }, ...regs].slice(0, 30);
    setRegs(next);
    write(REGS_KEY, next);
    setRgHandle('');
    setRgMail('');
    setRgConsent(false);
    setRegOk(true);
  };

  const submitMem = () => {
    if (memTitle.trim().length < 6) return;
    const next = [{ title: memTitle.trim(), note: memNote.trim() }, ...mems].slice(0, 30);
    setMems(next);
    write(MEM_KEY, next);
    setMemTitle('');
    setMemNote('');
  };

  const submitGb = () => {
    if (gbName.trim().length < 1 || gbNote.trim().length < 4) return;
    const next = [{ who: gbName.trim(), line: gbNote.trim(), at: new Date().toLocaleString() }, ...book].slice(0, 30);
    setBook(next);
    write(GB_KEY, next);
    setGbNote('');
    setGbOk(true);
  };

  return (
    <div className="dsp page">
      <header className="page__head">
        <SkinArt className="page__crest" src="/logos/dock-calendar.png" width={40} height={40} alt="" aria-hidden="true" />
        <div className="page__titles">
          <p className="eyebrow">calendar app · presence events</p>
          <h1 className="h1">Where the fleet shows up</h1>
        </div>
        <div className="page__chips">
          <span className="chip">{todayChip}</span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Presence modules">
        {TABS.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ MONTH ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'month'}>
        <div className="grid2">
          <div className="cal">
            <div className="cal__head">
              <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>{calLabel}</p>
              <span className="row">
                <button className="btn btn--ghost" type="button" aria-label="Previous month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}>‹</button>
                <button className="btn btn--ghost" type="button" aria-label="Next month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}>›</button>
              </span>
            </div>
            <div className="cal__grid">
              {DOW.map((d) => <div key={d} className="cal__dow">{d}</div>)}
              {cells.map((d, i) => {
                if (d === null) return <div key={i} className="cal__day cal__day--pad" />;
                const isToday = d === today.getDate() && view.getMonth() === today.getMonth() && view.getFullYear() === today.getFullYear();
                return <div key={i} className={'cal__day' + (isToday ? ' cal__day--today' : '')}>{d}</div>;
              })}
            </div>
          </div>
          <div className="stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>next up</p><span className="tag tag--muted">sample data</span></div>
            <ul className="rows">
              {NEXT_UP.map((n, i) => (
                <li key={i} className="lrow"><span className="lrow__t">{n.name}</span><span className="mono">{n.date}</span></li>
              ))}
            </ul>
            <p className="cap">Presence events are the in-person half of the fleet: rooms, check-ins, and the memory of the night.</p>
          </div>
        </div>
      </section>

      {/* ══ EVENTS ═════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'events'}>
        <div className="grid3">
          <div className="stat-tile"><span className="label">events</span><span className="val">4</span><span className="sub">On the presence sheet.</span></div>
          <div className="stat-tile"><span className="label">seats</span><span className="val">620</span><span className="sub">Across all rooms.</span></div>
          <div className="stat-tile"><span className="label">checked in</span><span className="val">{checkedIn.length}</span><span className="sub">Of the door list below.</span></div>
        </div>
        <div className="row row--between">
          <div className="grow"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>the sheet</p></div>
          <span className="mono">{visibleEvents.length} rows</span>
        </div>
        <input className="field" type="search" placeholder="filter events" aria-label="Filter events" value={evFilter} onChange={(e) => setEvFilter(e.target.value)} />
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Event</th><th>Venue</th><th className="r">Date</th><th className="r">Seats</th><th>State</th></tr></thead>
            <tbody>
              {visibleEvents.map((e, i) => (
                <tr key={i}>
                  <td>{e.name}</td><td>{e.venue}</td><td className="r mono">{e.date}</td><td className="r mono">{e.seats}</td>
                  <td>{e.state === 'confirmed' ? <span className="badge badge-live"><span className="lamp" aria-hidden="true" />confirmed</span> : <span className="tag tag--muted">{e.state}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ══ EVENTGOER ══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'eventgoer'}>
        <div className="row row--between">
          <div className="grow"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>door list · check-in</p><p className="cap">Guests are listed by ticket, never by name, until they check in.</p></div>
          <span className="mono">{checkedIn.length} / {GUESTS.length}</span>
        </div>
        <input className="field" type="search" placeholder="filter tickets" aria-label="Filter guests" value={ticketFilter} onChange={(e) => setTicketFilter(e.target.value)} />
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Ticket</th><th>Tier</th><th>Holder</th><th>State</th><th className="r">Check in</th></tr></thead>
            <tbody>
              {visibleGuests.map((g) => {
                const on = checkedIn.includes(g.ticket);
                return (
                  <tr key={g.ticket} className={on ? 'done' : undefined}>
                    <td className="mono">{g.ticket}</td>
                    <td>{g.guild ? <span className="chip">guild</span> : <span className="tag tag--muted">{g.tier}</span>}</td>
                    <td className="mono">holder on file</td>
                    <td>{on ? <span className="badge badge-live"><span className="lamp" aria-hidden="true" />in the room</span> : <span className="tag tag--muted">expected</span>}</td>
                    <td className="r"><button className="toggle" type="button" aria-pressed={on} aria-label={`Check in ${g.ticket}`} onClick={() => toggleCheckIn(g.ticket)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ══ FORMS ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'reg'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>registration</p>
            <div>
              <label className="lbl micro" htmlFor="rgHandle">Handle</label>
              <input className="field" id="rgHandle" type="text" placeholder="how you are known" value={rgHandle} onChange={(e) => setRgHandle(e.target.value)} />
            </div>
            <div>
              <label className="lbl micro" htmlFor="rgMail">Email</label>
              <input className="field" id="rgMail" type="email" placeholder="name@domain" value={rgMail} onChange={(e) => setRgMail(e.target.value)} />
            </div>
            <div>
              <label className="lbl micro" htmlFor="rgTier">Tier</label>
              <select className="field" id="rgTier" value={rgTier} onChange={(e) => setRgTier(e.target.value)}>
                <option>guest</option><option>guild</option><option>press</option>
              </select>
            </div>
            <label className="item" style={{ border: 0, padding: 0, gap: 9 }}>
              <input className="chk" type="checkbox" checked={rgConsent} onChange={(e) => setRgConsent(e.target.checked)} />
              <span>I agree to the presence terms · <span className="mono">name, handle and check-in only</span></span>
            </label>
            {regOk && <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Registered.</strong> Held in this browser until the door list is synced.</span></p>}
            <button className="btn btn--gold" type="button" onClick={submitReg}>Register</button>
          </div>
          <div className="stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>on the list</p><span className="mono">{regs.length}</span></div>
            {regs.length === 0 ? (
              <p className="empty">Nobody registered from this desk yet.</p>
            ) : (
              <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)', overflow: 'auto' }}>
                {regs.map((r, i) => (
                  <li key={i} className="lrow"><span className="mono">{r.handle}</span><span className="lrow__t mono">{r.email}</span><span className="tag tag--muted">{r.tier}</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* ══ MEMORIES ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'memories'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>what the rooms left behind</p>
            <ul className="tl">
              {[...mems.map((m) => ({ at: 'now', msg: m.title, tag: 'note', mine: true })), ...MEMORIES].map((m, i) => (
                <li key={i}><span className="mono mono--ink">{m.at.toUpperCase()}</span><p className="entry__m">{m.msg}</p><span className="tag tag--muted">{m.tag}</span></li>
              ))}
            </ul>
            <span className="tag tag--muted">sample data</span>
          </div>
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>keep one</p>
            <input className="field" type="text" placeholder="what happened" value={memTitle} onChange={(e) => setMemTitle(e.target.value)} />
            <textarea className="field" rows={3} placeholder="the detail worth keeping" value={memNote} onChange={(e) => setMemNote(e.target.value)} />
            <button className="btn btn--gold" type="button" onClick={submitMem} disabled={memTitle.trim().length < 6}>Keep memory</button>
          </div>
        </div>
      </section>

      {/* ══ GUILD ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'guild'}>
        <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>guild roles</p><span className="tag tag--muted">sample data</span></div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Role</th><th>Gate</th><th className="r">Holders</th><th>State</th></tr></thead>
            <tbody>
              {GUILD_ROLES.map((r, i) => (
                <tr key={i}>
                  <td>{r.role}</td><td>{r.gate}</td><td className="r mono">{r.holders}</td>
                  <td>{r.open ? <span className="badge badge-live"><span className="lamp" aria-hidden="true" />open</span> : <span className="tag tag--muted">held</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid2">
          <div className="term row row--between">
            <div className="grow"><p className="micro micro--ink">Guild opens on credential</p><p className="cap">Attendance earns the role automatically.</p></div>
            <button className="switch" type="button" aria-pressed={guildCred} aria-label="Guild opens on credential" onClick={() => setGuildCred((v) => !v)}>
              <span className="switch__track"><span className="switch__knob" /></span>
            </button>
          </div>
          <div className="term row row--between">
            <div className="grow"><p className="micro micro--ink">Press stays manual</p><p className="cap">A human decides who writes about us.</p></div>
            <button className="switch" type="button" aria-pressed={pressManual} aria-label="Press stays manual" onClick={() => setPressManual((v) => !v)}>
              <span className="switch__track"><span className="switch__knob" /></span>
            </button>
          </div>
        </div>
      </section>

      {/* ══ GUESTBOOK ══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'guestbook'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>sign it</p>
            <input className="field" type="text" placeholder="your handle" value={gbName} onChange={(e) => setGbName(e.target.value)} />
            <textarea className="field" rows={3} placeholder="a line for the room" value={gbNote} onChange={(e) => setGbNote(e.target.value)} />
            {gbOk && <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Signed.</strong> Kept in this browser.</span></p>}
            <button className="btn btn--gold" type="button" onClick={submitGb} disabled={gbName.trim().length < 1 || gbNote.trim().length < 4}>Sign the guestbook</button>
          </div>
          <div className="stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>the book</p><span className="mono">{book.length}</span></div>
            {book.length === 0 ? (
              <p className="empty">The book is empty. Be the first to sign it.</p>
            ) : (
              <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)', overflow: 'auto', maxHeight: 300 }}>
                {book.map((g, i) => (
                  <li key={i} className="entry" style={{ marginBottom: 8 }}>
                    <p className="mono mono--ink">{g.who}</p>
                    <p className="entry__m">{g.line}</p>
                    <p className="cap">{g.at}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <p className="page__foot">Presence is the part of the fleet that has a room number: a calendar, a list, a form, a memory and a guestbook.</p>
    </div>
  );
}
